import type { TimeSlot } from "@/lib/actions/actions";

import { LoadingCirclesHoriz } from "@frontend/components/elements/LoadingCirclesHoriz";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@frontend/shared/components/ui/avatar";
import { Button } from "@frontend/shared/components/ui/button";
import { format } from "date-fns";
import { Ellipsis } from "lucide-react";
import { useState } from "react";
import { useBooking } from "../context/BookingContext";
import { TimeSlotButton } from "./TimeSlotButton";

interface Provider {
  label: string;
  title?: string;
  degrees?: string;
  value: string;
  img: string | null;
}

interface ProviderCardProps {
  provider: Provider;
  availableSlots: TimeSlot[];
  currentDisplayDates: Date[];
  onTimeSlotSelect: (timeSlot: TimeSlot) => Promise<void>;
  isFetchingTimeSlots: boolean;
  timeZone: string;
}

export function ProviderCard({
  provider,
  availableSlots,
  currentDisplayDates,
  onTimeSlotSelect,
  isFetchingTimeSlots = false,
  timeZone,
}: ProviderCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { selectedDay } = useBooking();
  const t = useTranslations("Shop");
  const providerInitials = provider.label
    .split(" ")
    .map((n) => n[0])
    .join("");

  // Count total slots beyond the initial 3 per day
  const extraSlotsCount = currentDisplayDates.reduce((total, date) => {
    const daySlots = availableSlots.filter(
      (slot) =>
        // NOTE: We dont need to use timezones here because we are just comparing dates.
        format(new Date(slot.dateTime), "yyyy-MM-dd") ===
        format(date, "yyyy-MM-dd"),
    );
    return total + Math.max(0, daySlots.length - 3);
  }, 0);

  return (
    <div className="w-full flex flex-col border-b border-[#d5d5d5] rounded-none lg:p-2 lg:pb-4 sm:p-0 gap-4 sm:pb-6">
      <div className="flex lg:gap-4 sm:gap-2 lg:flex-row flex-col">
        <div className="flex lg:flex-col flex-row lg:gap-2 sm:gap-4 mb-4 items-center lg:min-w-[23%] lg:max-w-[23%] lg:overflow-hidden">
          <Avatar className="lg:w-32 lg:h-32 sm:w-24 sm:h-24 border border-[#d5d5d5]">
            <AvatarImage src={provider.img ?? undefined} />
            <AvatarFallback>{providerInitials}</AvatarFallback>
          </Avatar>
          <p className="text-sm font-semibold text-left lg:text-center gap-1 flex flex-col">
            {`${provider.title ?? ""} ${provider.label}`}
            {provider.degrees && (
              <span className="text-xs text-gray-500 font-normal">
                &nbsp;{provider.degrees}
              </span>
            )}
          </p>
        </div>
        {isFetchingTimeSlots ? (
          <div className="justify-center items-center w-full hidden lg:flex">
            <LoadingCirclesHoriz />
          </div>
        ) : availableSlots.length ? (
          <div className="hidden lg:grid grid-cols-5 gap-4 lg:px-2 sm:px-0 w-[77%]">
            {currentDisplayDates.map((date) => {
              const daySlots = availableSlots.filter(
                (slot) =>
                  format(new Date(slot.dateTime), "yyyy-MM-dd") ===
                  format(date, "yyyy-MM-dd"),
              );

              const displaySlots = isExpanded ? daySlots : daySlots.slice(0, 3);

              return (
                <div
                  key={date.toISOString() + provider.label}
                  className="flex flex-col gap-2"
                >
                  {daySlots.length === 0 ? (
                    <div className="flex items-start justify-center h-full">
                      <Ellipsis size={24} className="text-gray-400" />
                    </div>
                  ) : (
                    <>
                      {displaySlots.map((slot) => (
                        <div
                          key={slot.dateTime.toString() + provider.label}
                          className="flex flex-col gap-2"
                        >
                          <TimeSlotButton
                            timeSlot={slot}
                            timeZone={timeZone}
                            onSelect={async () => {
                              await onTimeSlotSelect({
                                ...slot,
                                timezone: slot.timezone ?? timeZone,
                              });
                            }}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        {isFetchingTimeSlots ? (
          <div className="flex justify-center items-center w-full lg:hidden">
            <LoadingCirclesHoriz />
          </div>
        ) : availableSlots.length ? (
          <div className="lg:hidden w-full">
            {(() => {
              const daySlots = availableSlots.filter(
                (slot) =>
                  format(new Date(slot.dateTime), "yyyy-MM-dd") ===
                  format(selectedDay, "yyyy-MM-dd"),
              );

              return daySlots.length === 0 ? (
                <div className="flex items-start justify-center h-full">
                  <div className="flex justify-center items-center w-full">
                    <p className="text-xs text-gray-900 text-center w-4/5">
                      {t("ThisProviderDoesNotHaveAvailableSlots")}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {daySlots.map((slot) => (
                    <TimeSlotButton
                      key={slot.dateTime.toString() + provider.label}
                      timeSlot={slot}
                      onSelect={async () => {
                        await onTimeSlotSelect(slot);
                      }}
                      timeZone={timeZone}
                    />
                  ))}
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex justify-center items-center w-full">
            <p className="text-xs text-gray-900 text-center w-4/5">
              {t("ThisProviderDoesNotHaveAvailableSlots")}
            </p>
          </div>
        )}
      </div>

      {extraSlotsCount > 0 && (
        <div className="lg:w-[77%] sm:w-full  justify-center self-end lg:pl-4 sm:pl-0 hidden lg:block">
          <Button
            variant="link"
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs font-light self-center underline underline-offset-1"
          >
            {isExpanded ? t("ShowLess") : `${t("Expand")} (${extraSlotsCount})`}
          </Button>
        </div>
      )}
    </div>
  );
}
